import { Link } from "react-router-dom";
import { Clock, TrendingUp, TrendingDown, Zap, ExternalLink } from "lucide-react";
import { SourceBadge, LeagueChip, CategoryTag } from "@/components/badges";
import { relativeTime, absoluteTime } from "@/lib/format";

function OddsChip({ oc }) {
  if (!oc || oc.direction === "flat") return null;
  const down = oc.direction === "down";
  const Icon = down ? TrendingDown : TrendingUp;
  const color = down ? "#FF4D4D" : "#00E5A3";
  return (
    <div data-testid="odds-chip" className="mt-3 flex flex-wrap items-center gap-2 rounded-lg border border-[#232D42] bg-[#151B2B] px-3 py-2">
      <Icon size={14} style={{ color }} />
      <span className="text-xs font-mono text-slate-300">{oc.market}</span>
      <span className="text-xs font-mono text-slate-500">{oc.line_before}</span>
      <span className="text-xs" style={{ color }}>→</span>
      <span className="text-xs font-mono font-bold" style={{ color }}>{oc.line_after}</span>
      {oc.priced_in ? (
        <span className="ml-auto text-[10px] font-mono uppercase tracking-wider text-[#38BDF8]">market adjusted</span>
      ) : (
        <span className="ml-auto flex items-center gap-1 text-[10px] font-mono uppercase tracking-wider text-[#FFB020]"><Zap size={10} /> market lagging</span>
      )}
    </div>
  );
}

export default function EventCard({ event, isNew }) {
  const hasPlayer = event.player_id && event.player_id !== "null";
  const Wrapper = hasPlayer ? Link : "div";
  const wrapperProps = hasPlayer ? { to: `/players/${event.player_id}` } : {};
  return (
    <Wrapper
      {...wrapperProps}
      data-testid={`event-card-${event.id}`}
      className={`block bg-[#0F1420] border border-[#232D42] rounded-xl p-4 sm:p-5 hover:border-[#00E5A3]/40 transition-all duration-200 ${isNew ? "sq-slide-in" : ""}`}
    >
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <LeagueChip league={event.league} />
        <CategoryTag category={event.category} />
        <SourceBadge official={event.official} source={event.source} />
        {event.source_url && (
          <span role="link" tabIndex={0}
            data-testid={`event-source-link-${event.id}`}
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); window.open(event.source_url, "_blank", "noopener"); }}
            className="inline-flex items-center gap-1 text-[11px] font-mono text-[#00E5A3] hover:underline cursor-pointer">
            view citation <ExternalLink size={11} />
          </span>
        )}
        {event.certainty && (
          <span className="text-[11px] font-mono uppercase tracking-wider text-slate-500">
            certainty: {event.certainty}
          </span>
        )}
      </div>

      <h3 className="font-heading text-base sm:text-lg font-bold text-slate-100 leading-snug">
        {event.headline}
      </h3>
      <p className="text-sm text-slate-400 mt-1.5 leading-relaxed">{event.detail}</p>

      <OddsChip oc={event.odds_context} />

      <div className="flex flex-wrap items-center justify-between gap-2 mt-3 pt-3 border-t border-[#232D42]">
        <div className="flex items-center gap-2 text-sm">
          <span className="font-semibold text-slate-200">{event.player_name}</span>
          <span className="text-slate-500">·</span>
          <span className="text-slate-400 font-mono text-xs">{event.team_abbr} · {event.position}</span>
        </div>
        <div className="flex items-center gap-1.5 text-slate-500 font-mono text-[11px]">
          <Clock size={12} />
          <span className="text-[#00E5A3]">{relativeTime(event.timestamp_utc)}</span>
          <span>· {absoluteTime(event.timestamp_utc)}</span>
        </div>
      </div>
    </Wrapper>
  );
}
